"use client";

import type { ReactNode } from "react";
import { LuCheck, LuCopy, LuInfo, LuShare, LuSquarePlus } from "react-icons/lu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/context/ToastContext";
import { copyToClipboardWithToast } from "@/utils/copy-to-clipboard";

// Instrucciones de instalación en iPhone/iPad, compartidas por la app móvil
// (`InstallPromptProvider`) y el admin (`AdminInstallBanner`). Safari no tiene
// `beforeinstallprompt`, así que no hay forma de instalar desde código: solo
// queda explicar el paso manual. El primer paso es abrir en Safari porque desde
// el navegador interno de WhatsApp u otras apps "Agregar a inicio" no existe.

function StepRow({
  number,
  icon,
  title,
  description,
  children,
}: {
  number: number;
  icon: ReactNode;
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Badge
        color="orange"
        tone="solid"
        shape="pill"
        className="h-7 w-7 shrink-0 justify-center p-0 text-sm font-bold"
      >
        {number}
      </Badge>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-fg">
          <span className="shrink-0 text-fg-muted">{icon}</span>
          {title}
        </p>
        {description && (
          <p className="mt-0.5 text-sm text-fg-muted">{description}</p>
        )}
        {children && <div className="mt-2">{children}</div>}
      </div>
    </div>
  );
}

function OpenInSafariStep({ number }: { number: number }) {
  const { showToast } = useToast();

  function handleCopyLink() {
    copyToClipboardWithToast(window.location.href, showToast);
  }

  return (
    <StepRow
      number={number}
      icon={<LuInfo className="h-4 w-4" aria-hidden />}
      title="Abre este enlace en Safari"
      description={
        'Si abriste desde WhatsApp u otra app, toca los tres puntos y elige "Abrir en Safari" o copia el enlace y pégalo ahí.'
      }
    >
      <Button
        variant="outline"
        size="sm"
        className="w-fit gap-1.5"
        onClick={handleCopyLink}
      >
        <LuCopy className="h-3.5 w-3.5" aria-hidden />
        Copiar enlace
      </Button>
    </StepRow>
  );
}

export function IosInstallDialog({
  open,
  onOpenChange,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Al tocar "Entendido"; el diálogo ya se cierra solo. */
  onDone?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Instala Orquesti en tu iPhone o iPad</DialogTitle>
          <DialogDescription>
            Solo se puede instalar desde Safari. Son cuatro pasos.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <OpenInSafariStep number={1} />
          <StepRow
            number={2}
            icon={<LuShare className="h-4 w-4" aria-hidden />}
            title="Toca el ícono de Compartir"
            description="Está en la barra de Safari, arriba o abajo según el dispositivo."
          />
          <StepRow
            number={3}
            icon={<LuSquarePlus className="h-4 w-4" aria-hidden />}
            title='Elige "Agregar a inicio"'
          />
          <StepRow
            number={4}
            icon={<LuCheck className="h-4 w-4" aria-hidden />}
            title='Confirma tocando "Agregar"'
          />
        </div>
        <p className="text-sm text-fg-muted">
          Va a aparecer un ícono de Orquesti en tu pantalla de inicio y se va a
          abrir como una app.
        </p>
        <Button
          variant="strong"
          fullWidth
          onClick={() => {
            onOpenChange(false);
            onDone?.();
          }}
        >
          Entendido
        </Button>
      </DialogContent>
    </Dialog>
  );
}
